import React, { useEffect, useState, useCallback, useRef } from "react";

import { useLayoutStore } from "~/store";
import {
  SignInDialogUi,
  DialogUi,
  ConsentPopup,
  CharacterCount,
  InvalidCharactersError,
} from "~/components";
import { useNavigate, useLocation } from "react-router-dom";
import { useAcid } from "../../hooks/useAcid";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { MorningIcon } from "../../components/Icon/Icons/MorningIcon";
import { AfternoonIcon } from "../../components/Icon/Icons/AfternoonIcon";
import { EveningIcon } from "../../components/Icon/Icons/EveningIcon";
import { AnytimeIcon } from "../../components/Icon/Icons/AnytimeIcon";
import { buildStructuredBeverageData } from "../../utils/beveragePayload";
import {
  getStoredBeverageConsent,
  shouldResetBeverageStateForAcidChange,
} from "../../utils/beverageSession";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import { hasDisallowedCharacters } from "../../utils/beverageTextValidation";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";
import { beverageCategories } from "./constants/beverage-categories";
import { beveragePreferences } from "./constants/beverage-preferences";
import { beveragePlaceholders } from "./constants/beverage-placeholders";
import { saveBeverageV5ConsentOnly } from "./consentFlow";

import styles from "./Beverage.module.css";

// pixels from bottom to trigger footer adjustment
const FOOTER_PROXIMITY_THRESHOLD = 200;
// milliseconds between scroll checks
const SCROLL_THROTTLE_DELAY = 100;

const timeOfDayOptions = [
  { name: "Morning", Icon: MorningIcon },
  { name: "Afternoon", Icon: AfternoonIcon },
  { name: "Evening", Icon: EveningIcon },
  { name: "Anytime", Icon: AnytimeIcon },
];
export const BeverageV5 = () => {
  const cid = "beverage";
  const navigate = useNavigate();
  const location = useLocation();
  const { acid } = useAcid();
  const { isResetting, setContentClassName } = useLayoutStore();
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPreferences, setSelectedPreferences] = useState({});
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState({});
  const [additionalDetails, setAdditionalDetails] = useState({});
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedCategories, setSavedCategories] = useState(new Set());
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [isConsentSaving, setIsConsentSaving] = useState(false);
  const [categoryToAutoSave, setCategoryToAutoSave] = useState(null);
  const [savingCategories, setSavingCategories] = useState(new Set());

  // Prevents duplicate saves before React updates state by using refs, which update immediately and block rapid re-entry.
  const saveCategoryLockRef = useRef(false);
  const saveConsentLockRef = useRef(false);
  const previousAcidRef = useRef(acid);
  const currentAcidRef = useRef(acid);

  const resetBeverageStateForAcidChange = useCallback(() => {
    setShowSignInDialog(false);
    setSelectedCategory(null);
    setSelectedPreferences({});
    setSelectedTimeOfDay({});
    setAdditionalDetails({});
    setIsModalSaving(false);
    setSaveError(null);
    setSavedCategories(new Set());
    setShowConsentPopup(false);
    setIsConsentSaving(false);
    setCategoryToAutoSave(null);
    setSavingCategories(new Set());
    saveCategoryLockRef.current = false;
    saveConsentLockRef.current = false;
  }, []);

  useEffect(() => {
    currentAcidRef.current = acid;
    if (shouldResetBeverageStateForAcidChange(previousAcidRef.current, acid)) {
      resetBeverageStateForAcidChange();
    }
    previousAcidRef.current = acid;
  }, [acid, resetBeverageStateForAcidChange]);

  // Send page view analytics event on mount
  useEffect(() => {
    return sendPageViewWhenConsentReady(
      "Beverage V5 Page",
      "Passions-FavBevs",
      "beverages5",
    );
  }, []);

  const saveConsentOnly = useCallback(
    async (optedOut) => {
      if (!acid) {
        setShowConsentPopup(false);
        setShowSignInDialog(true);
        return false;
      }

      if (saveConsentLockRef.current) {
        return false;
      }
      saveConsentLockRef.current = true;
      setIsConsentSaving(true);

      try {
        const requestAcid = acid;
        const saved = await saveBeverageV5ConsentOnly({
          acid: requestAcid,
          optedOut,
          actionWebInvoke,
          actionUrl: actions["marriott-passions-v3/save-beverage-preferences"],
          shouldPersist: () => currentAcidRef.current === requestAcid,
        });
        if (!saved) {
          return false;
        }
        setShowConsentPopup(false);
        console.log("✅ Saved consent value to CDP");
        return true;
      } catch (error) {
        console.error("Error saving consent:", error);
        return false;
      } finally {
        setIsConsentSaving(false);
        saveConsentLockRef.current = false;
      }
    },
    [acid],
  );

  // Handle consent popup accept (user opts IN to personalization = NOT opted out)
  const handleConsentAccept = useCallback(async () => {
    await saveConsentOnly(false);
  }, [saveConsentOnly]);

  // Handle consent popup decline (user opts OUT of personalization)
  const handleConsentDecline = useCallback(async () => {
    await saveConsentOnly(true);
  }, [saveConsentOnly]);

  /**
   * Dismissing the dialog without making a choice defaults the session to
   * opted out. Unlike accept/decline, closing must always work immediately
   * — an outage or slow network shouldn't leave the guest stuck unable to
   * dismiss this dialog. The opted-out value is still persisted best-effort
   * in the background; if that fails, consent stays unanswered so the
   * popup correctly reappears on the next save attempt instead of silently
   * recording a choice that never reached CDP.
   */
  const handleConsentClose = useCallback(() => {
    setShowConsentPopup(false);
    saveConsentOnly(true);
  }, [saveConsentOnly]);

  // Restore beverage selections from sessionStorage on mount (after login redirect)
  useEffect(() => {
    const savedData = sessionStorage.getItem("pendingBeverageV5Selections");
    if (!savedData) {
      return;
    }

    try {
      const parsed = JSON.parse(savedData);
      const {
        version,
        timestamp,
        sessionId,
        preferences,
        timeOfDay,
        details,
        saved,
        pendingSaveCategory,
      } = parsed;

      // Validate version
      if (version !== 1) {
        console.warn("⚠️ Incompatible beverage selections version:", version);
        sessionStorage.removeItem("pendingBeverageV5Selections");
        return;
      }

      // Check if data is stale (older than 10 minutes)
      const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
      const age = Date.now() - timestamp;
      if (age > MAX_AGE_MS) {
        console.warn(
          `⚠️ Beverage selections expired (age: ${Math.round(age / 1000)}s)`,
        );
        sessionStorage.removeItem("pendingBeverageV5Selections");
        return;
      }

      // Validate session ID matches (prevents cross-session pollution)
      const currentSessionId = sessionStorage.getItem("beverageV5SessionId");
      if (sessionId && currentSessionId && sessionId !== currentSessionId) {
        console.warn("⚠️ Session ID mismatch, ignoring stale selections");
        sessionStorage.removeItem("pendingBeverageV5Selections");
        return;
      }

      // Validate data structure
      if (!preferences || typeof preferences !== "object") {
        console.warn("⚠️ Invalid beverage selections data structure");
        sessionStorage.removeItem("pendingBeverageV5Selections");
        return;
      }

      // All validations passed - restore state
      setSelectedPreferences(preferences);
      setSelectedTimeOfDay(timeOfDay || {});
      setAdditionalDetails(details || {});
      setSavedCategories(new Set(saved || []));

      if (pendingSaveCategory) {
        setCategoryToAutoSave(pendingSaveCategory);
      }

      // Clear the saved selections after successful restoration
      sessionStorage.removeItem("pendingBeverageV5Selections");
      console.log(
        `✅ Restored beverage selections (age: ${Math.round(age / 1000)}s, session: ${sessionId})`,
      );
    } catch (error) {
      console.error("❌ Failed to restore beverage selections:", error);
      sessionStorage.removeItem("pendingBeverageV5Selections");
    }
  }, []);

  useEffect(() => {
    if (isResetting) {
      return;
    }

    setContentClassName(styles.parentContent);
    console.log(
      "Beverage component mounted, categories:",
      beverageCategories.length,
    );
  }, [isResetting, setContentClassName]);

  // Handle scroll to avoid footer overlap with throttling
  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeout) {
        return;
      }

      const timeout = setTimeout(() => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Calculate if we're near the bottom
        const nearBottom =
          scrollTop + windowHeight >=
          documentHeight - FOOTER_PROXIMITY_THRESHOLD;
        setIsNearFooter(nearBottom);
        setScrollTimeout(null);
      }, SCROLL_THROTTLE_DELAY);

      setScrollTimeout(timeout);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [scrollTimeout]);

  // Send analytics event when beverage modal opens
  useEffect(() => {
    if (selectedCategory) {
      sendAnalyticsPageViewEvent(
        `Beverage V5 Modal: How do you enjoy your ${selectedCategory.name}?`,
        "Passions-FavBevs",
        "beverages5",
      );
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (showSignInDialog) {
      sendAnalyticsPageViewEvent(
        "Beverage V5 Page Modal: Sign In",
        "Passions-FavBevs",
        "beverages5",
      );
    }
  }, [showSignInDialog]);

  useEffect(() => {
    if (showConsentPopup) {
      sendAnalyticsPageViewEvent(
        "Beverage V5 Page Modal: Consent",
        "Passions-FavBevs",
        "beverages5",
      );
    }
  }, [showConsentPopup]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
  };

  const togglePreference = (category, preference) => {
    setSelectedPreferences((prev) => {
      const categoryPrefs = prev[category] || [];
      const isSelected = categoryPrefs.includes(preference);

      return {
        ...prev,
        [category]: isSelected
          ? categoryPrefs.filter((p) => p !== preference)
          : [...categoryPrefs, preference],
      };
    });
  };

  const toggleTimeOfDay = (timeOfDay) => {
    if (!selectedCategory) return;
    const categoryName = selectedCategory.name;

    setSelectedTimeOfDay((prev) => {
      const categoryTimes = prev[categoryName] || [];
      const isSelected = categoryTimes.includes(timeOfDay);

      let newValue = isSelected
        ? categoryTimes.filter((i) => i !== timeOfDay)
        : [...categoryTimes, timeOfDay];

      // We want to toggle items properly, so we will select anytime OR other options. Statements below are responsible for that
      if (timeOfDay === "Anytime") {
        newValue = newValue.filter((i) => i === timeOfDay);
      } else {
        newValue = newValue.filter((i) => i !== "Anytime");
      }

      if (newValue.length === 3) {
        newValue = ["Anytime"];
      }

      return {
        ...prev,
        [categoryName]: newValue,
      };
    });
  };

  const saveCategoryPreferences = useCallback(
    async (categoryObj) => {
      if (saveCategoryLockRef.current) {
        return false;
      }
      // Check if already saving this category
      if (savingCategories.has(categoryObj.name)) {
        return false;
      }

      if (isModalSaving) {
        return false; // Prevent double-submission
      }

      saveCategoryLockRef.current = true;
      setSavingCategories((prev) => new Set([...prev, categoryObj.name]));
      setIsModalSaving(true);
      setSaveError(null);

      try {
        const requestAcid = acid;
        // Prepare structured data for the current category
        const currentCategoryData = {
          category: categoryObj.name,
          types:
            selectedPreferences[categoryObj.name]?.map((type) => ({
              type: type,
              additionalDetails: additionalDetails[categoryObj.name] || "",
              timeOfDay: selectedTimeOfDay[categoryObj.name] || [],
            })) || [],
        };

        // Save only beverage preferences, not consent value
        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          {
            acid: requestAcid,
            structuredBeverageData: [currentCategoryData],
          },
        );
        if (currentAcidRef.current !== requestAcid) {
          return false;
        }

        // Store preferences in sessionStorage for persistence
        const savedData = buildStructuredBeverageData(
          selectedPreferences,
          additionalDetails,
          selectedTimeOfDay,
        );
        sessionStorage.setItem(
          "savedBeverageV5Preferences",
          JSON.stringify(savedData),
        );

        // Mark the current category as saved
        setSavedCategories((prev) => new Set([...prev, categoryObj.name]));

        // Only close modal if it's open and matches the saved category
        if (selectedCategory && selectedCategory.name === categoryObj.name) {
          handleCloseModal();
        }
        return true;
      } catch (error) {
        console.error("Error saving beverage preferences:", error);
        setSaveError("Failed to save preferences. Please try again.");
        return false;
      } finally {
        setSavingCategories((prev) => {
          const next = new Set(prev);
          next.delete(categoryObj.name);
          return next;
        });
        setIsModalSaving(false);
        saveCategoryLockRef.current = false;
      }
    },
    [
      acid,
      selectedPreferences,
      additionalDetails,
      selectedTimeOfDay,
      isModalSaving,
      selectedCategory,
      savingCategories,
    ],
  );

  const handleSavePreferences = async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    if (selectedCategory) {
      // Save beverages first
      const saved = await saveCategoryPreferences(selectedCategory);

      // After beverages are saved, show consent popup if not already answered for the current acid.
      if (saved && !getStoredBeverageConsent().hasResponded) {
        setShowConsentPopup(true);
      }
    }
  };

  // Keeps a reference to the latest saveCategoryPreferences function so this effect doesn't re-run unnecessarily after every save.
  const saveCategoryPreferencesRef = useRef(saveCategoryPreferences);
  useEffect(() => {
    saveCategoryPreferencesRef.current = saveCategoryPreferences;
  });

  /**
   * Auto-save effect: only acid/categoryToAutoSave actually becoming
   * available should trigger this, nothing else.
   */
  useEffect(() => {
    const autoSave = async () => {
      if (acid && categoryToAutoSave) {
        const categoryObj = beverageCategories.find(
          (c) => c.name === categoryToAutoSave,
        );
        if (categoryObj) {
          console.log(`🔄 Auto-saving pending category: ${categoryToAutoSave}`);

          setCategoryToAutoSave(null);
          const saved = await saveCategoryPreferencesRef.current(categoryObj);

          if (saved && !getStoredBeverageConsent().hasResponded) {
            setShowConsentPopup(true);
          }
        }
      }
    };

    autoSave();
  }, [acid, categoryToAutoSave]);

  const done = useCallback(() => {
    navigate(`/beverage5/thank-you${location.search}`);
  }, [navigate, location.search]);

  const onCloseDialog = useCallback(
    () => setShowSignInDialog(false),
    [setShowSignInDialog],
  );

  const onSubmitDialog = useCallback(() => {
    // Generate or retrieve session ID for this beverage selection session
    let sessionId = sessionStorage.getItem("beverageV5SessionId");
    if (!sessionId) {
      sessionId = `bev-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("beverageV5SessionId", sessionId);
    }

    // Save current selections to sessionStorage before redirect with versioning
    const selectionsToSave = {
      version: 1, // Schema version for future compatibility
      timestamp: Date.now(), // For expiration checking
      sessionId: sessionId, // Unique ID for this selection session
      preferences: selectedPreferences,
      timeOfDay: selectedTimeOfDay,
      details: additionalDetails,
      saved: Array.from(savedCategories),
      pendingSaveCategory: selectedCategory?.name,
    };

    try {
      sessionStorage.setItem(
        "pendingBeverageV5Selections",
        JSON.stringify(selectionsToSave),
      );
      console.log(
        `💾 Saved beverage selections (session: ${sessionId}, items: ${Object.keys(selectedPreferences).length})`,
      );
    } catch (error) {
      console.error("❌ Failed to save beverage selections:", error);
      // Continue with login even if save fails
    }
    /**
     * Keep the dialog open until navigation begins to avoid briefly showing
     * the underlying page before the redirect completes.
     */
  }, [
    selectedPreferences,
    selectedTimeOfDay,
    additionalDetails,
    savedCategories,
    selectedCategory,
  ]);

  // Check if at least one preference has been saved
  const hasSavedPreferences = savedCategories.size > 0;

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.headerTitle}>
            Tap to select your favorite drinks
          </h1>

          {hasSavedPreferences && (
            <div
              className={`${styles.doneButtonContainer} ${isNearFooter ? styles.nearFooter : ""}`}
            >
              <button
                data-testid="beverage-done"
                className={`${styles.doneButton} custom_click_track`}
                data-custom_click_track_value={`Beverage Footer|Done|internal`}
                onClick={done}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.categoryGrid}>
          {beverageCategories.map((category, index) => {
            const isSaved = savedCategories.has(category.name);
            const isSavingCategory = savingCategories.has(category.name);

            return (
              <div
                key={index}
                data-testid="beverage-category"
                className={`${styles.categoryCard} ${isSaved ? styles.categoryCardSelected : ""} custom_click_track`}
                data-custom_click_track_value={`Beverages|Category Selection: ${category.name}|internal`}
                onClick={() => handleCategoryClick(category)}
              >
                <div className={styles.categoryImageWrapper}>
                  <img
                    src={isSaved ? category.activeIcon : category.icon}
                    alt={category.name}
                    className={styles.categoryImage}
                    loading="lazy"
                    onError={(e) => {
                      console.error(
                        `Failed to load image for ${category.name}:`,
                        category.image,
                      );
                      e.target.style.backgroundColor = "#f0f0f0";
                    }}
                  />
                  <div className={styles.categoryOverlay}>
                    <span className={styles.categoryName}>{category.name}</span>
                    {isSavingCategory && (
                      <div className={styles.savingBadge}>
                        <span>Saving...</span>
                      </div>
                    )}
                    {!isSavingCategory && isSaved && (
                      <>
                        <div className={styles.addedBadge} data-testid="beverage-category-added">
                          <span>Added</span>

                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="16px"
                            height="16px"
                          >
                            <path
                              className={styles.svgPath}
                              strokeMiterlimit="10"
                              strokeWidth="2"
                              d="M21 6L9 18 4 13"
                            />
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedCategory && !showSignInDialog && !showConsentPopup && (
        <DialogUi
          data-testid="beverage-details-dialog"
          onClose={handleCloseModal}
          disableOverlayClose={true}
          header={null}
          body={
            <div className={styles.beverageModal}>
              <button
                data-testid="beverage-details-close"
                className={`${styles.closeButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Close Category Modal: ${selectedCategory.name}|internal`}
                onClick={handleCloseModal}
                aria-label={`Close ${selectedCategory.name} preferences`}
              >
                ✕
              </button>
              <h2 className={styles.modalQuestion}>
                How do you enjoy your{" "}
                {selectedCategory.name.substring(0, 1).toUpperCase() +
                  selectedCategory.name.substring(1)}
                ?
              </h2>

              {beveragePreferences[selectedCategory.name]?.length > 0 && (
                <div className={styles.preferencesSection}>
                  <h3 className={styles.sectionLabel}>Type</h3>
                  <div className={styles.optionsGrid}>
                    {beveragePreferences[selectedCategory.name].map(
                      (preference, index) => {
                        const isSelected =
                          selectedPreferences[selectedCategory.name]?.includes(
                            preference,
                          );
                        return (
                          <button
                            key={index}
                            data-testid="beverage-type-option"
                            aria-pressed={Boolean(isSelected)}
                            className={`${styles.optionButton} ${isSelected ? styles.optionButtonSelected : ""} custom_click_track`}
                            data-custom_click_track_value={`Beverages|Selected Category: ${selectedCategory.name}, Preference Selection: ${preference}|internal`}
                            onClick={() =>
                              togglePreference(
                                selectedCategory.name,
                                preference,
                              )
                            }
                          >
                            {preference}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
              )}

              <div
                className={`${styles.additionalSectionsContainer} ${
                  selectedPreferences[selectedCategory.name]?.length > 0
                    ? styles.additionalSectionsVisible
                    : styles.additionalSectionsHidden
                }`}
              >
                {selectedPreferences[selectedCategory.name]?.length ? (
                  <div className={styles.typeSpecificSection}>
                    <div className={styles.preferencesSection}>
                      <h4 className={styles.sectionLabel}>
                        Tell us more (optional)
                      </h4>
                      <textarea
                        data-testid="beverage-details-input"
                        className={styles.additionalDetailsInput}
                        placeholder={
                          beveragePlaceholders[selectedCategory.name] ||
                          `Tell us more about your ${selectedCategory.name.toLowerCase()} preferences...`
                        }
                        value={additionalDetails[selectedCategory.name] || ""}
                        onChange={(e) =>
                          setAdditionalDetails((prev) => ({
                            ...prev,
                            [selectedCategory.name]: e.target.value,
                          }))
                        }
                        maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                        rows={2}
                      />
                      <CharacterCount
                        value={additionalDetails[selectedCategory.name]}
                        max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                      />
                      <InvalidCharactersError
                        value={additionalDetails[selectedCategory.name]}
                      />
                    </div>

                    <p className={styles.hint}>
                      Please do not include profanity, unintelligible
                      information, personally identifiable information, or
                      offensive or inappropriate content.
                    </p>

                    <div className={styles.preferencesSection}>
                      <h4 className={styles.sectionLabel}>
                        Time of day (optional)
                      </h4>
                      <div className={styles.timeOfDayGrid}>
                        {timeOfDayOptions.map(({ name, Icon }) => {
                          const isSelected =
                            selectedTimeOfDay[selectedCategory.name]?.includes(
                              name,
                            );
                          return (
                            <button
                              key={name}
                              data-testid="beverage-time-option"
                              aria-pressed={Boolean(isSelected)}
                              className={`${styles.timeOfDayButton} ${isSelected ? styles.timeOfDayButtonSelected : ""} custom_click_track`}
                              data-custom_click_track_value={`Beverages|Selected Category: ${selectedCategory.name}, Time Of Day Selection: ${name}|internal`}
                              onClick={() => toggleTimeOfDay(name)}
                            >
                              <Icon
                                alt={name}
                                className={styles.timeOfDayIcon}
                              />
                              <span className={styles.timeOfDayLabel}>
                                {name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {saveError && (
                <div className={styles.errorMessage}>{saveError}</div>
              )}

              <button
                data-testid="beverage-details-save"
                className={`${styles.saveButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Save Category Preferences: ${selectedCategory.name}|internal`}
                onClick={handleSavePreferences}
                disabled={
                  isModalSaving ||
                  !selectedPreferences[selectedCategory.name]?.length ||
                  hasDisallowedCharacters(additionalDetails[selectedCategory.name])
                }
              >
                {isModalSaving ? "Saving..." : "Save"}
              </button>

              <p className={styles.privacyText}>
                Preferences can be updated at any time in my{" "}
                <a
                  href="https://www.marriott.com/loyalty/myAccount/default.mi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.privacyLink}
                >
                  Bonvoy Account Profile
                </a>
                , and I can learn more about how my data is collected and used
                in the{" "}
                <a
                  href="https://www.marriott.com/about/privacy.mi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.privacyLink}
                >
                  Global Privacy Statement
                </a>
                .
              </p>
            </div>
          }
          footer={null}
          modalStyle={styles.customModalDialog}
          modalBodyStyle={styles.customModalBody}
          modalHeaderStyle={styles.hideDefaultHeader}
          closeButtonStyle={styles.hideDefaultCloseButton}
        />
      )}
      {showSignInDialog && (
        <SignInDialogUi
          onClose={onCloseDialog}
          onSubmit={onSubmitDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage5/preferences${stripAcidFromSearch(location.search)}`}
        />
      )}
      {showConsentPopup && (
        <ConsentPopup
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
          onClose={handleConsentClose}
          loading={isConsentSaving}
        />
      )}
    </>
  );
};
